import { useCallback, useEffect, useState } from 'react';
import { PodcastFeedSummary } from '../types';
import {
  CreatePodcastFeedRequest,
  podcastFeedsApi,
} from '../api/podcastFeeds';

interface UsePodcastFeedsReturn {
  feeds: PodcastFeedSummary[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  createFeed: (request: CreatePodcastFeedRequest) => Promise<PodcastFeedSummary>;
  deleteFeed: (feedId: string) => Promise<void>;
}

export default function usePodcastFeeds(): UsePodcastFeedsReturn {
  const [feeds, setFeeds] = useState<PodcastFeedSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await podcastFeedsApi.list();
      setFeeds(response.items);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to load podcast feeds';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, []);

  const createFeed = useCallback(
    async (request: CreatePodcastFeedRequest) => {
      const created = await podcastFeedsApi.create(request);
      const summary: PodcastFeedSummary = {
        id: created.id,
        userId: created.userId,
        name: created.name,
        description: created.description,
        published: created.published,
        feedToken: created.feedToken,
        feedUrl: created.feedUrl,
        itemCount: created.itemCount,
        createdAt: created.createdAt,
        updatedAt: created.updatedAt,
      };
      setFeeds((prev) => [summary, ...prev]);
      return summary;
    },
    []
  );

  const deleteFeed = useCallback(async (feedId: string) => {
    await podcastFeedsApi.remove(feedId);
    setFeeds((prev) => prev.filter((feed) => feed.id !== feedId));
  }, []);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  return { feeds, loading, error, refetch, createFeed, deleteFeed };
}
